/**
 * One subscription to "what became of this submission" (CHARTER §7.194).
 *
 * The server pushes a `command` frame whenever a command's status moves -- the
 * projection derives it from the obligation and effect rows the tail wrote --
 * and `App.tsx` hands every frame here. The components that used to ask
 * `/api/commands/<id>` on a clock (400 ms, 1200 ms, 2 s, one clock each)
 * listen instead. One fetch on mount says where the command stands *now*;
 * frames say where it goes. A frame that arrives while that fetch is in flight
 * is the newer fact, so the fetch's answer is dropped when it lands.
 *
 * Nothing here is a copy of the status. The module holds listeners and the
 * hook holds the last frame it was shown; both are rebuilt from the next fetch
 * or frame, and neither is written anywhere.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type CommandFrame, type CommandStatus } from "./api";

type Listener = (frame: CommandFrame) => void;
const listeners = new Set<Listener>();
const resyncListeners = new Set<() => void>();

export const commandFeed = {
  /** A `command` frame off the stream. */
  publish(frame: CommandFrame): void {
    for (const listener of [...listeners]) listener(frame);
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  /**
   * The stream came back after a gap. Whatever moved meanwhile was pushed to
   * nobody, so every open reading fetches once more.
   */
  resync(): void {
    for (const listener of [...resyncListeners]) listener();
  },
  onResync(listener: () => void): () => void {
    resyncListeners.add(listener);
    return () => { resyncListeners.delete(listener); };
  },
};

/** The approval cards holding a command: the frame's own list, or the same list read off a fetched status's effects. */
export function heldKeys(status: CommandStatus | null | undefined): string[] {
  if (!status) return [];
  if (status.held) return status.held;
  return status.effects.filter(effect => effect.state === "held").map(effect => `effect:${effect.id}`);
}

export interface CommandReading {
  status: CommandStatus | null;
  /** A read that failed. A failed read is not a failed action. */
  error: string | null;
  retry: () => void;
}

/**
 * Where one submission stands, for a caller that is not a component.
 *
 * `useCommandStatus` below is the same thing as a hook, and this is the same
 * thing as a plain subscribe/release pair, because the form state machine
 * (`surfacekit/form.js`) asks for one: it runs inside the sandbox too, where
 * there is no React. One read now, then the frames; a frame that overtakes the
 * read is the later fact and the read's answer is dropped, for the reason given
 * at the top of this file.
 *
 * A read that fails is not reported. What this exists to say is "the command
 * settled"; a status nobody could fetch has not settled, and the caller is
 * still subscribed for the frame that says it did.
 */
export function watchCommand(
  obligationId: string,
  fn: (status: CommandStatus) => void,
): () => void {
  let live = true;
  let pushed = 0;
  const stop = commandFeed.subscribe(frame => {
    if (!live || frame.obligation_id !== obligationId) return;
    pushed += 1;
    fn(frame);
  });
  const read = () => {
    const before = pushed;
    api.commandStatus(obligationId).then(body => {
      if (live && pushed === before) fn(body);
    }).catch(() => {});
  };
  read();
  const stopResync = commandFeed.onResync(read);
  return () => { live = false; stop(); stopResync(); };
}

/**
 * Where one submission stands: one fetch, then the frames about it.
 *
 * `active` false pauses everything -- no fetch, no listener -- which is what a
 * paused surface asks for. The reading is keyed by obligation, so a component
 * that is handed another id never shows the previous one's status.
 */
export function useCommandStatus(obligationId: string | null | undefined, active = true): CommandReading {
  const [reading, setReading] = useState<{ id: string; status: CommandStatus } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(n => n + 1), []);
  useEffect(() => {
    if (!obligationId || !active) return;
    let live = true;
    let pushed = 0;
    const stop = commandFeed.subscribe(frame => {
      if (frame.obligation_id !== obligationId) return;
      pushed += 1;
      setReading({ id: obligationId, status: frame });
      setError(null);
    });
    const read = () => {
      const before = pushed;
      api.commandStatus(obligationId).then(body => {
        // A frame overtook this read: the frame is the later fact.
        if (!live || pushed !== before) return;
        setReading({ id: obligationId, status: body });
        setError(null);
      }).catch(err => {
        if (live) setError(ApiError.fromUnknown(err).detail);
      });
    };
    read();
    const stopResync = commandFeed.onResync(read);
    return () => { live = false; stop(); stopResync(); };
  }, [obligationId, active, attempt]);
  return { status: reading && reading.id === obligationId ? reading.status : null, error, retry };
}
